// The standard way to show a container slot: the one app's widget, framed inline
// on the page under a header the host draws, or nothing at all. Which widget that
// is, and every rule behind it, is `useSlotEmbeds`'; the frame and its platform
// behaviour are `SlotContainer`'s.

import { t } from '@lingui/core/macro';
import type { SlotId } from '@luminovo/apps-types';
import {
    colorSystem,
    Flexbox,
    stringifySearch,
    TertiaryButton,
    Text,
    useRouteMatches,
    useSearch,
} from '@luminovo/design-system';
import { WorkflowIcon } from 'lucide-react';
import { type ReactElement, type ReactNode, useEffect, useMemo, useRef } from 'react';

import { useSlotEmbeds } from '../hooks/useSlotEmbeds';
import { SlotContainer } from './SlotContainer';

export interface AppContainerSlotProps {
    slotId: SlotId;
    /** What this slot hands its widget, as the slot registry declares it. */
    params: Readonly<Record<string, unknown>>;
    /** Slug of the tenant the product page is showing, handed to the widget. */
    tenant: string;
    /** Locale used to resolve app-authored names, and handed to the widget. */
    locale: string;
    /**
     * Wraps the header and frame in the page's own chrome, such as a divider above
     * them. Called only when there is a widget to show, so a page with no app
     * renders none of its chrome either.
     */
    renderLayout?: (frame: ReactNode) => ReactNode;
}

export function AppContainerSlot({
    slotId,
    params,
    tenant,
    locale,
    renderLayout = (frame) => frame,
}: AppContainerSlotProps): ReactElement | null {
    const [embed] = useSlotEmbeds(slotId, params, locale);
    // The product page around the slot. A container's widget does not own the
    // URL, but it is told where the user is, like any other widget. The leaf
    // match is the page on screen; the fragment is not available to a component
    // below the route, and a container has no use for it.
    const pathname = useRouteMatches().at(-1)?.pathname ?? '/';
    const search = useSearch({ strict: false, select: stringifySearch });
    const paramsKey = useMemo(() => JSON.stringify(params), [params]);

    // A tenant with no app here should see the page it had before.
    if (embed === undefined) return null;

    const appName = embed.assignment.app.name;
    return (
        <>
            {renderLayout(
                <>
                    <ContainerHeader appName={appName} widgetName={embed.name} />
                    <SlotContainer
                        key={embed.key}
                        assignment={embed.assignment}
                        appsOrigin={embed.appsOrigin}
                        slotId={slotId}
                        tenant={tenant}
                        params={params}
                        location={{ pathname, search, hash: '' }}
                        locale={locale}
                        navigation="embedded"
                        errorFallback={(retry) => (
                            <ContainerErrorRow appName={appName} paramsKey={paramsKey} retry={retry} />
                        )}
                    />
                </>,
            )}
        </>
    );
}

// Drawn by the host, outside the frame, so a widget cannot pass itself off as the
// product page around it, such as a sign-in prompt in the customer sidebar: what
// sits under this header is an app, and it says which one.
function ContainerHeader({ appName, widgetName }: { appName: string; widgetName: string }): ReactElement {
    return (
        <Flexbox
            gap={6}
            sx={{ alignItems: 'center', minWidth: 0, color: colorSystem.neutral[6], paddingBottom: '4px' }}
        >
            <WorkflowIcon size={14} aria-hidden style={{ flexShrink: 0 }} />
            <Text variant="body-small-semibold" color={colorSystem.neutral[7]} showEllipsis>
                {appName}
            </Text>
            <Text variant="body-small" color={colorSystem.neutral[6]} showEllipsis>
                {widgetName}
            </Text>
        </Flexbox>
    );
}

// In place of the frame when it failed, whatever the reason. The slot stays with
// this app: another app taking over after a failure would make what the page
// shows depend on a timeout.
function ContainerErrorRow({
    appName,
    paramsKey,
    retry,
}: {
    appName: string;
    paramsKey: string;
    retry: () => void;
}): ReactElement {
    // New params, such as the next customer on a page that stays mounted, are a
    // new chance: the failure was about the data the widget had before.
    const failedWithRef = useRef(paramsKey);
    useEffect(() => {
        if (failedWithRef.current === paramsKey) return;
        failedWithRef.current = paramsKey;
        retry();
    }, [paramsKey, retry]);

    return (
        <Flexbox gap={8} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {t`${appName} could not load.`}
            </Text>
            <TertiaryButton size="small" onClick={retry}>
                {t`Retry`}
            </TertiaryButton>
        </Flexbox>
    );
}
